import React from 'react';

import { KeyboardArrowDown as ChevronIcon } from '@mui/icons-material';
import { Box, Chip, Menu, MenuItem, Paper, Stack, Typography } from '@mui/material';

import { DateFormatPatterns } from '@clearverify/constants';
import type { Nullable } from '@clearverify/types';
import { formatDate, getUserFullName } from '@clearverify/utils';

import { type EmployeeDataInfoFragment, UserStatus } from 'src/api';
import type { EmployeeInviteLinkArgs, ResendInviteScope } from 'src/hooks/employee';
import type { SxProp } from 'src/types/mui';
import { formatMaskedSsnNumber } from 'src/utils/ssn';

import { InfoBlock } from '../../shared';

import { EmployeeDataSummaryActionsMenu } from './EmployeeDataSummaryActionsMenu';

type EmployeeDataSummaryCardProps = {
  entry: EmployeeDataInfoFragment;
  enableAdminControls?: boolean;
  inviteLinkArgs?: Nullable<EmployeeInviteLinkArgs>;
  employeeSsnEncrypted?: string;
  inviteEmailOptionsScope?: 'admin' | 'employerScope';
  sendInviteScope?: ResendInviteScope;
  onResendInvite?: (employeeId: string, inviteLinkArgs?: EmployeeInviteLinkArgs | null) => void;
  onInviteSent?: () => void;
  onDataDelete?: (employeeId: string) => void;
  onDataDeleteCancel?: (employeeId: string) => void;
};

export const EmployeeDataSummaryCard: React.FC<EmployeeDataSummaryCardProps> = ({
  entry,
  enableAdminControls = false,
  inviteLinkArgs,
  employeeSsnEncrypted,
  inviteEmailOptionsScope,
  sendInviteScope,
  onResendInvite,
  onInviteSent,
  onDataDelete,
  onDataDeleteCancel,
}) => {
  // Single menu shared by both status chips — they're mutually exclusive, `kind` picks the action.
  const [menu, setMenu] = React.useState<{ anchor: HTMLElement; kind: 'requested' | 'deleted' }>();
  const closeMenu = () => setMenu(undefined);

  const handleMenuAction = () => {
    if (entry.id) {
      if (menu?.kind === 'requested') onDataDelete?.(entry.id);
      else onDataDeleteCancel?.(entry.id);
    }
    closeMenu();
  };

  // Chevron clicks land on the delete icon, body clicks on the chip root — anchor to the chip either way.
  const openMenu = (kind: 'requested' | 'deleted') => (event: React.MouseEvent<HTMLElement>) =>
    setMenu({
      anchor: event.currentTarget.closest<HTMLElement>('.MuiChip-root') ?? event.currentTarget,
      kind,
    });

  return (
    <Paper variant="outlined" sx={summaryPaperStyles}>
      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            label={`SSN: ${formatMaskedSsnNumber(entry.last4OfSsn)}`}
            variant="outlined"
            color="default"
          />
          {entry.user ? (
            userStatusChip(entry.user.status)
          ) : (
            <Chip size="small" label="No Clear Verify user" variant="outlined" color="default" />
          )}
          {!!entry.dataDeletedAt && (
            <Chip
              size="small"
              label="Data Deleted"
              color="error"
              {...(enableAdminControls && {
                onClick: openMenu('deleted'),
                onDelete: openMenu('deleted'),
                deleteIcon: <ChevronIcon />,
              })}
            />
          )}
          {!!entry.dataDeleteRequestSent && (
            <Chip
              size="small"
              label="Data Delete Requested"
              color="warning"
              {...(enableAdminControls && {
                onClick: openMenu('requested'),
                onDelete: openMenu('requested'),
                deleteIcon: <ChevronIcon />,
              })}
            />
          )}
          <Chip
            size="small"
            label={`${entry.employmentRecords.length} employment record${
              entry.employmentRecords.length === 1 ? '' : 's'
            }`}
            variant="outlined"
          />
        </Stack>
        <EmployeeDataSummaryActionsMenu
          entry={entry}
          enableAdminControls={enableAdminControls}
          inviteLinkArgs={inviteLinkArgs}
          employeeSsnEncrypted={employeeSsnEncrypted}
          inviteEmailOptionsScope={inviteEmailOptionsScope}
          sendInviteScope={sendInviteScope}
          onResendInvite={onResendInvite}
          onInviteSent={onInviteSent}
        />
      </Stack>

      {entry.user ? (
        <Box mt={2}>
          <Typography variant="subtitle2" gutterBottom>
            Clear Verify user
          </Typography>
          <InfoBlock.Container gap={1}>
            <InfoBlock.Item title="Name" value={getUserFullName(entry.user)} />
            <InfoBlock.Item title="Email" value={entry.user.email} />
            <InfoBlock.Item title="Phone" value={entry.user.phoneNumber} />
            <InfoBlock.Item title="Created at" value={formatCreatedAt(entry.user.createdAt)} />
          </InfoBlock.Container>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" mt={2}>
          No Clear Verify user is linked to this person yet.
        </Typography>
      )}

      <Menu
        anchorEl={menu?.anchor}
        open={!!menu}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MenuItem onClick={handleMenuAction}>
          {menu?.kind === 'requested' ? 'Fulfill Data Delete Request' : 'Cancel Data Delete'}
        </MenuItem>
      </Menu>
    </Paper>
  );
};

const summaryPaperStyles: SxProp = {
  padding: 3,
  borderRadius: '10px',
};

const formatCreatedAt = (value: string | Date | null | undefined) => {
  if (!value) return null;
  const iso = value instanceof Date ? value.toISOString() : value;
  return formatDate(iso, DateFormatPatterns.fullDateWithTime);
};

const userStatusChip = (status: UserStatus | undefined) => {
  if (!status) return null;
  if (status === UserStatus.Active) {
    return <Chip size="small" label="Clear Verify: Active" color="success" />;
  }
  if (status === UserStatus.Pending) {
    return <Chip size="small" label="Clear Verify: Pending" color="warning" />;
  }
  return <Chip size="small" label="Clear Verify: Inactive" color="default" variant="outlined" />;
};
